import { useState } from 'react';
import { useMutation } from '@apollo/client';

import { useAppSelector } from 'app/store';
import FormRequestBlock from 'components/FormRequestBlock';
import { getRequestShopByURLInput, REQUEST_STORE_BY_URL } from 'app/graphql/cart-api';
import { INPUT_PATTERN, trackSegmentEvent } from 'app/utils';
import { ACTION, SOURCE } from 'app/analytics';
import { selectUserInfo } from 'app/store/user';

const DEFAULT_INPUT_VALUE = '';

interface Props {
  onRequestComplete: () => Promise<unknown>;
}

const StoreUrlRequest = ({ onRequestComplete }: Props) => {
  const { isReadOnly } = useAppSelector(selectUserInfo) || {};

  const [submitting, setSubmitting] = useState(false);
  const [inputValue, setInputValue] = useState(DEFAULT_INPUT_VALUE);
  const [storeRequestError, setStoreRequestError] = useState<string>('');

  const [mutateFunction] = useMutation(REQUEST_STORE_BY_URL);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (isReadOnly) {
      return;
    }

    const variables = getRequestShopByURLInput(inputValue);

    if (variables) {
      setSubmitting(true);

      try {
        // Actually send the request
        try {
          await mutateFunction({ variables });
        } catch (e: unknown) {
          if (e instanceof Error) {
            // Show a specific error for when users reach their limit
            if (e.message.includes('requestStoreByURL limit reached')) {
              // Use local state because redux is overkill for this
              setStoreRequestError(
                "You've reached your limit for requesting stores. Please reach out to dev@rye.com for more information and to increase your limit.",
              );
            } else if (e.message.includes('Store has already been added to Rye')) {
              setStoreRequestError(
                'This store has already been added to Rye and cannot be added again.',
              );
            } else {
              setStoreRequestError(
                'Something went wrong, please try again. Please reach out to dev@rye.com if the issue persists.',
              );
            }
          }
        }

        await onRequestComplete();
        setInputValue(DEFAULT_INPUT_VALUE);
      } finally {
        setSubmitting(false);
      }
    } else {
      // @TODO add notification UI
      alert('Please provide correct product URL');
    }
    trackSegmentEvent(SOURCE.REQUEST_STORE_URL_MODULE, ACTION.CLICK, 'request_button', {
      store_url: inputValue,
    });
  };

  return (
    <FormRequestBlock
      title="Add store URL"
      submitValue="Request"
      placeholder="Enter store URL"
      text={<p>Enter a store URL that you want to request access for</p>}
      loading={submitting}
      required={true}
      type="url"
      pattern={INPUT_PATTERN.URL}
      inputValue={inputValue}
      disabled={isReadOnly}
      onChange={setInputValue}
      onSubmit={handleSubmit}
      error={storeRequestError}
    />
  );
};

export default StoreUrlRequest;
